/**
 * Lightweight HTML sanitizer for personal use.
 * Allows safe formatting tags; strips everything else (scripts, event handlers, etc.)
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "hr",
  "strong", "b", "em", "i", "u", "strike", "del", "ins",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "blockquote", "pre", "code",
  "a", "img",
  "div", "span",
  "table", "thead", "tbody", "tr", "th", "td",
  "sup", "sub",
]);

const ALLOWED_ATTRS = new Set([
  "href", "src", "alt", "title", "class", "id",
  "colspan", "rowspan",
  "style", "data-img-id", "data-zoom", "contenteditable",
]);

const VOID_TAGS = new Set([
  "br", "hr", "img", "input", "meta", "link", "area", "base", "col", "embed",
  "param", "source", "track", "wbr",
]);

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Parse a simple HTML string and rebuild it with only allowed tags/attributes.
 * This is not a full parser but handles common personal-use HTML well.
 * For production with untrusted users, swap to DOMPurify.
 */
export function sanitizeHtml(raw) {
  if (!raw || typeof raw !== "string") return "";

  // Quick path: if no tags at all, return escaped
  if (!/<[a-zA-Z/][^>]*>/.test(raw)) return escapeHtml(raw);

  const out = [];
  let i = 0;

  while (i < raw.length) {
    const lt = raw.indexOf("<", i);
    if (lt === -1) {
      out.push(escapeHtml(raw.slice(i)));
      break;
    }

    // Emit text before tag
    if (lt > i) {
      out.push(escapeHtml(raw.slice(i, lt)));
    }

    const gt = raw.indexOf(">", lt);
    if (gt === -1) {
      // Malformed: unclosed tag — treat rest as text
      out.push(escapeHtml(raw.slice(lt)));
      break;
    }

    const fullTag = raw.slice(lt + 1, gt);
    const isClose = fullTag.startsWith("/");
    const isComment = fullTag.startsWith("!");

    if (isComment) {
      // Strip comments
      if (fullTag.startsWith("!--")) {
        const endComment = raw.indexOf("-->", gt);
        i = endComment === -1 ? raw.length : endComment + 3;
        continue;
      }
      i = gt + 1;
      continue;
    }

    const tagNameMatch = fullTag.match(/^\/?([a-zA-Z0-9]+)/);
    const tagName = tagNameMatch ? tagNameMatch[1].toLowerCase() : null;

    if (!tagName || !ALLOWED_TAGS.has(tagName)) {
      // Strip disallowed tag entirely
      i = gt + 1;
      continue;
    }

    if (isClose) {
      out.push(`</${tagName}>`);
      i = gt + 1;
      continue;
    }

    // Parse quoted and unquoted HTML attributes without splitting names into chars.
    const attrPart = fullTag.slice(tagNameMatch[1].length);
    const safeAttrs = [];
    const attrRegex = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attrPart)) !== null) {
      const attrName = attrMatch[1].toLowerCase();
      const attrValue = attrMatch[2] ?? attrMatch[3] ?? attrMatch[4] ?? "";
      if (ALLOWED_ATTRS.has(attrName)) {
        if (attrName === "href" || attrName === "src") {
          // Only allow http/https/mailto/data urls
          if (
            /^https?:\/\//i.test(attrValue) ||
            /^mailto:/i.test(attrValue) ||
            /^data:image\//i.test(attrValue) ||
            attrValue.startsWith("/") ||
            attrValue.startsWith("#")
          ) {
            safeAttrs.push(`${attrName}="${escapeHtml(attrValue)}"`);
          }
        } else {
          safeAttrs.push(`${attrName}="${escapeHtml(attrValue)}"`);
        }
      }
    }

    const attrStr = safeAttrs.length ? " " + safeAttrs.join(" ") : "";

    if (VOID_TAGS.has(tagName)) {
      out.push(`<${tagName}${attrStr} />`);
    } else {
      out.push(`<${tagName}${attrStr}>`);
    }

    i = gt + 1;
  }

  return out.join("");
}

export default sanitizeHtml;